/**
 * Cookie-based storage adapter for Supabase auth.
 *
 * Solves: localStorage is origin-scoped, so sessions created on
 * app.zenmusicgroup.com are invisible on label.zenmusicgroup.com.
 * Cookies scoped to `.zenmusicgroup.com` are shared across subdomains.
 *
 * Only activates on *.zenmusicgroup.com. On localhost / other hosts,
 * falls back to localStorage so dev workflow is unchanged.
 *
 * Handles values > 3500 bytes by chunking across multiple cookies
 * (browser limit is ~4096 bytes per cookie).
 */

const CHUNK_SIZE = 3500; // bytes, leaves room for name + attributes

function isSharedDomain(): boolean {
  if (typeof window === "undefined") return false;
  return window.location.hostname.endsWith(".zenmusicgroup.com");
}

function getCookieDomain(): string {
  return ".zenmusicgroup.com";
}

function cookieOptions(): string {
  const parts = [`path=/`, `max-age=${60 * 60 * 24 * 365}`, `SameSite=Lax`];
  if (isSharedDomain()) {
    parts.push(`domain=${getCookieDomain()}`);
  }
  if (window.location.protocol === "https:") {
    parts.push("Secure");
  }
  return parts.join("; ");
}

function deleteOptions(): string {
  const parts = [`path=/`, `max-age=0`, `SameSite=Lax`];
  if (isSharedDomain()) {
    parts.push(`domain=${getCookieDomain()}`);
  }
  if (window.location.protocol === "https:") {
    parts.push("Secure");
  }
  return parts.join("; ");
}

// Raw cookie map — values are NOT decoded here. Stored values are already
// percent-encoded (URL-safe), and chunked values must be JOINED before decoding
// so a chunk boundary landing inside a %XX escape can't break decodeURIComponent.
function getRawCookies(): Record<string, string> {
  const result: Record<string, string> = {};
  for (const pair of document.cookie.split(";")) {
    const idx = pair.indexOf("=");
    if (idx < 0) continue;
    const key = pair.slice(0, idx).trim();
    if (key) result[key] = pair.slice(idx + 1);
  }
  return result;
}

// `encodedValue` is already percent-encoded by the caller; store it verbatim so
// chunks can be sliced/joined on the encoded string without corruption.
function setRawCookie(name: string, encodedValue: string): void {
  document.cookie = `${name}=${encodedValue}; ${cookieOptions()}`;
}

function deleteRawCookie(name: string): void {
  document.cookie = `${name}=; ${deleteOptions()}`;
}

/**
 * Cookie storage with automatic chunking.
 * Implements the same interface as localStorage so Supabase can use it.
 */
export const cookieStorage = {
  getItem(key: string): string | null {
    if (!isSharedDomain()) return localStorage.getItem(key);

    const cookies = getRawCookies();

    // Unchunked value
    if (key in cookies) return decodeURIComponent(cookies[key]);

    // Chunked values: key.0, key.1, … — join the raw (still-encoded) pieces back
    // into the full encoded string, THEN decode once.
    const chunks: string[] = [];
    let i = 0;
    while (`${key}.${i}` in cookies) {
      chunks.push(cookies[`${key}.${i}`]);
      i++;
    }
    return chunks.length > 0 ? decodeURIComponent(chunks.join("")) : null;
  },

  setItem(key: string, value: string): void {
    if (!isSharedDomain()) {
      localStorage.setItem(key, value);
      return;
    }

    // Clean up any existing chunks or base key
    this.removeItem(key);

    // Chunk on the ENCODED string so every cookie's actual byte length stays under
    // the limit. (The old code sliced the raw value then encoded each chunk, which
    // could balloon a 3500-char JSON slice well past 4 KB → the browser silently
    // dropped that cookie → a corrupted/truncated session on read.)
    const encoded = encodeURIComponent(value);
    if (encoded.length <= CHUNK_SIZE) {
      setRawCookie(key, encoded);
    } else {
      let i = 0;
      let offset = 0;
      while (offset < encoded.length) {
        setRawCookie(`${key}.${i}`, encoded.slice(offset, offset + CHUNK_SIZE));
        offset += CHUNK_SIZE;
        i++;
      }
    }
  },

  removeItem(key: string): void {
    if (!isSharedDomain()) {
      localStorage.removeItem(key);
      return;
    }

    // Delete base key
    deleteRawCookie(key);

    // Delete any chunks
    const cookies = getRawCookies();
    let i = 0;
    while (`${key}.${i}` in cookies) {
      deleteRawCookie(`${key}.${i}`);
      i++;
    }
  },
};
